import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Pencil, MapPin, Bed, Bath, Maximize, ImageIcon } from 'lucide-react';
import type { PropertyFeatures } from '../PropertyFeaturesDialog';

interface Props {
  contactPerson: string; setContactPerson: (v: string) => void;
  contactPhone: string; setContactPhone: (v: string) => void;
  contactEmail: string; setContactEmail: (v: string) => void;

  // Summary data
  titleEn: string;
  listingType: 'sale' | 'rent';
  propertyType: string;
  city: string;
  subCity: string;
  price: string;
  currency: string;
  areaSqm: string;
  bedrooms: string;
  bathrooms: string;
  features: PropertyFeatures;
  images: { url: string; is_primary: boolean }[];

  onJump: (step: number) => void;
}

export function Step5Review(p: Props) {
  const formatPrice = () => {
    if (!p.price) return '—';
    const n = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(p.price));
    return p.currency === 'USD' ? `$ ${n}` : `Br ${n}`;
  };
  const totalFeatures = Object.values(p.features).reduce((s, a) => s + (a?.length || 0), 0);
  const cover = p.images.find(i => i.is_primary)?.url || p.images[0]?.url;

  return (
    <>
      <h3 className="text-sm font-semibold">Contact Information</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div><Label>Contact Person</Label><Input value={p.contactPerson} onChange={e => p.setContactPerson(e.target.value)} placeholder="Your name" /></div>
        <div><Label>Phone *</Label><Input value={p.contactPhone} onChange={e => p.setContactPhone(e.target.value)} placeholder="+251..." /></div>
        <div><Label>Email</Label><Input value={p.contactEmail} onChange={e => p.setContactEmail(e.target.value)} placeholder="email@example.com" /></div>
      </div>

      <div className="border-t pt-5">
        <h3 className="text-sm font-semibold mb-3">Review Your Listing</h3>
        <Card>
          <CardContent className="p-4 flex gap-4">
            <div className="h-24 w-32 shrink-0 rounded-md bg-muted overflow-hidden flex items-center justify-center">
              {cover ? <img src={cover} alt="" className="h-full w-full object-cover" /> : <ImageIcon className="h-6 w-6 text-muted-foreground" />}
            </div>
            <div className="flex-1 min-w-0 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge>{p.listingType === 'sale' ? 'For Sale' : 'For Rent'}</Badge>
                <Badge variant="outline" className="capitalize">{p.propertyType}</Badge>
              </div>
              <div className="font-semibold text-base truncate">{p.titleEn || 'Untitled listing'}</div>
              <div className="text-lg font-display font-bold text-primary">{formatPrice()}</div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{p.city || '—'}{p.subCity ? `, ${p.subCity}` : ''}</span>
                {p.bedrooms && <span className="flex items-center gap-1"><Bed className="h-3 w-3" />{p.bedrooms}</span>}
                {p.bathrooms && <span className="flex items-center gap-1"><Bath className="h-3 w-3" />{p.bathrooms}</span>}
                {p.areaSqm && <span className="flex items-center gap-1"><Maximize className="h-3 w-3" />{p.areaSqm} m²</span>}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <ReviewRow label="Purpose & Type" onEdit={() => p.onJump(1)} value={`${p.listingType === 'sale' ? 'For Sale' : 'For Rent'} · ${p.propertyType}`} />
          <ReviewRow label="Location" onEdit={() => p.onJump(1)} value={p.city ? `${p.city}${p.subCity ? ', ' + p.subCity : ''}` : 'Not set'} />
          <ReviewRow label="Price" onEdit={() => p.onJump(2)} value={formatPrice()} />
          <ReviewRow label="Photos" onEdit={() => p.onJump(3)} value={`${p.images.length} uploaded`} />
          <ReviewRow label="Features" onEdit={() => p.onJump(4)} value={`${totalFeatures} selected`} />
          <ReviewRow label="Contact" onEdit={() => p.onJump(5)} value={p.contactPhone || 'Phone required'} />
        </div>
      </div>
    </>
  );
}

function ReviewRow({ label, value, onEdit }: { label: string; value: string; onEdit: () => void }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-lg border bg-muted/30 px-3 py-2">
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
        <div className="text-sm font-medium truncate">{value}</div>
      </div>
      <button type="button" onClick={onEdit} className="text-primary hover:underline text-xs flex items-center gap-1 shrink-0">
        <Pencil className="h-3 w-3" /> Edit
      </button>
    </div>
  );
}
